<template>
  <svg
    :x="x"
    :y="y"
    :width="width"
    :height="height"
    preserveAspectRatio="none"
    :viewBox="viewBox"
  >
    <g id="soundwave">
      <rect
        v-for="r in rectangles"
        :key="r.key"
        :x="r.x"
        :y="r.y"
        :height="r.height"
        :width="r.width"
        stroke="none"
        fill="white"
        rx="0.5"
        ry="0.5"
      ></rect>
    </g>
  </svg>
</template>
<script>
import { fadeInOut, pow, normalizeArray } from '../../util/svgUtils.js'

import { cutSilence, normalDistribute } from '../../util/autoEnhance'

const minHeightOfInnerBar = 0.0075
const minBarHeight = 0.005

export default {
  name: 'FourierViz',
  props: {
    soundData: {
      type: Object,
      required: true,
    },
    fourier: {
      type: Array,
      required: true,
    },
    x: {
      type: Number,
      required: true,
    },
    y: {
      type: Number,
      required: true,
    },
    width: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    visualOptions: {
      type: Object,
      required: false,
    },
  },
  data: function() {
    return {
      widthVisual: 700,
      heightVisual: 250,
    }
  },
  computed: {
    viewBox: function() {
      return [0, 0, this.widthVisual, this.heightVisual].join(' ')
    },
    rectangles: function() {
      // we make all values positive here. This might be quesionable
      let inputArray = this.soundData.straight.thick.map((d) => Math.abs(d))

      inputArray = this.visualOptions.cutSilence
        ? cutSilence(inputArray)
        : inputArray
      inputArray = this.visualOptions.enhanceAudio
        ? normalDistribute(inputArray)
        : pow(normalizeArray(inputArray), 2)

      inputArray = fadeInOut(inputArray, 0.05)
      inputArray = normalizeArray(inputArray)

      if (this.fourier.length == 0) return
      const gap = 0.5
      let rects = []

      for (let t = 0; t < inputArray.length; t++) {
        const volume = inputArray[t] // between 0 and 1 [?]
        const amplitudes = getAmplitudesAt(this.fourier, t / inputArray.length) // between 0 and 1, sum of them is 1

        let y = 0
        let i = 0
        for (let amplitude of amplitudes) {
          let height = volume * amplitude
          // set a minimum height for the first bar in each soundwave
          if (i == 0) {
            height = Math.max(minHeightOfInnerBar, height)
          }
          if (height < minBarHeight) {
            continue
          }
          rects.push({
            x: t,
            width: 1 - gap,
            y: y,
            height,
            i: i,
            key: t + '-' + i,
          })
          y += height //+ gap;

          i++
        }
      }
      const width = this.widthVisual * 1
      const outerWidthPerEntry = width / inputArray.length // width including gap
      const gapInPixels = outerWidthPerEntry * gap
      const heightOfAllGaps = gapInPixels * this.fourier[0].length
      const heightFactor =
        (this.heightVisual / 2 - heightOfAllGaps) /
        Math.max(...rects.map((r) => r.y + r.height))
      const middleGap = 1
      const scaled = rects.map((r) => {
        return {
          x: r.x * outerWidthPerEntry,
          width: r.width * outerWidthPerEntry,
          y: gapInPixels * r.i + r.y * heightFactor + middleGap,
          height: r.height * heightFactor,
          key: r.key,
        }
      })
      const normalRects = scaled.map((r) => {
        r = Object.assign({}, r)
        r.y += this.heightVisual / 2
        r.key += '-n'
        return r
      })
      const inversedRects = scaled.map((r) => {
        r.y = this.heightVisual / 2 - r.y - r.height
        r.key += '-i'
        return r
      })

      const allRects = normalRects.concat(inversedRects)
      return allRects
    },
    backgroundRects: function() {
      return []
    },
  },

  // TODO: Can probably be removed 27.02.21
  // returns what percentage of the sound can from which amplitude at one point in the recording
  // beforeUpdate: function() {
  //   const soundwave = document.getElementById('soundwave')
  //   soundwave.classList.remove('animate-soundwave')
  //   setTimeout(() => {
  //     soundwave.classList.add('animate-soundwave')
  //   }, 0)
  // },
}

function getAmplitudesAt(fourier, percentageInRecording) {
  const raw = fourier[Math.floor(percentageInRecording * fourier.length)] // Todo add easing function
  const sum = raw.reduce((a, b) => a + b)

  return raw.map((a) => a / sum)
}
</script>
